import React, { useEffect, useState } from 'react';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import { visuallyHidden } from '@mui/utils';
import { Member } from '@/utils/types';
import { TextField } from '@mui/material';
import { Cancel, Notifications } from '@mui/icons-material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';

interface Data {
  id: string;
  user: string;
  memberStatus: string;
  topPath: string;
  school: string;
  year: number | string;
  lastSession: string;
  membershipType: string;
}

function createData(
  id: string,
  user: string,
  memberStatus: string,
  topPath: string,
  school: string,
  year: number | string,
  lastSession: string,
  membershipType: string
): Data {
  return {
    id,
    user,
    memberStatus,
    topPath,
    school,
    year,
    lastSession,
    membershipType
  };
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = 'asc' | 'desc';

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string },
) => number {
  return order === 'desc'
    ? (a, b) => {
        const result = descendingComparator(a, b, orderBy);
        if (result === 0) {
          const userKey = "user" as Key;
          // If primary sort (membershipType) is equal, sort by user
          return -descendingComparator(a, b, userKey);
        }
        return result;
      }
    : (a, b) => {
        const result = -descendingComparator(a, b, orderBy);
        if (result === 0) {
          const userKey = "user" as Key;
          // If primary sort (membershipType) is equal, sort by user
          return -descendingComparator(a, b, userKey);
        }
        return result;
      };
}

interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
  showInInviteTable: boolean;
}


const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#9daec250',
    color: theme.palette.common.black,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const headCells: readonly HeadCell[] = [
  {
    id: 'user',
    numeric: false,
    disablePadding: false,
    label: 'User',
    showInInviteTable: true
  },
  {
    id: 'id',
    numeric: false,
    disablePadding: false,
    label: 'Email',
    showInInviteTable: true
  },
  {
    id: 'memberStatus',
    numeric: false,
    disablePadding: false,
    label: 'Member Status',
    showInInviteTable: false
  },
  {
    id: 'membershipType',
    numeric: false,
    disablePadding: false,
    label: 'Account Type',
    showInInviteTable: true
  },
  {
    id: 'topPath',
    numeric: false,
    disablePadding: false,
    label: 'Top Path',
    showInInviteTable: false
  },
  {
    id: 'school',
    numeric: false,
    disablePadding: false,
    label: 'Department',
    showInInviteTable: true
  },
  {
    id: 'year',
    numeric: true,
    disablePadding: false,
    label: 'Year',
    showInInviteTable: true
  },
  {
    id: 'lastSession',
    numeric: false,
    disablePadding: false,
    label: 'Last Session',
    showInInviteTable: false
  },
];

interface MembersTableHeadProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  isInviteTable: boolean;
}

function MembersTableHead(props: MembersTableHeadProps) {
  const { order, orderBy, rowCount, onRequestSort } =
    props;
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.filter(x => props.isInviteTable ? x.showInInviteTable : x.id != 'id').map((headCell) => (
          <StyledTableCell
            key={headCell.id}
            align={'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}
interface MembersTableToolbarProps {
  searchBar: React.ReactElement;
  filterBar: React.ReactElement;
  heading: string;
}
function MembersTableToolbar(props: MembersTableToolbarProps) {
  return (
    <Toolbar
      sx={[
        {
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
        },
      ]}
    >
      <Typography
        sx={{ flex: '1 1 100%', textAlign: 'left' }}
        variant="h6"
        id="tableTitle"
        component="div"
      >
        {props.heading}
      </Typography>
      <Tooltip title="Searchbar">
        {props.searchBar}
      </Tooltip>
      <Tooltip title="Filter">
        {props.filterBar}
      </Tooltip>
    </Toolbar>
  );
}

interface MemberTableProps {
  members: Member[];
  isInviteTable?: boolean
  remindMembers?: (members: Member[]) => void
  deleteMembers?: (members: Member[]) => void
  memberSelected?: (member: Member | null) => void
  error?: boolean
  setoverlayComp?: (val: boolean) => void
}

const formatDate = (date: string): string => {
  const dateNoTime = date.split(" ")[0];
  const splitDate = dateNoTime.split("-");
  return `${splitDate[1]}/${splitDate[2]}/${splitDate[0]}`
}

export default function MembersTable(props: MemberTableProps) {
  const rows: Data[] = props.members.map(member =>
    createData(member.email,
      member.name,
      member.id ? "active" : "invited",
      member.topPath != "" ? member.topPath : "-",
      member.school != "" ? member.school : "-",
      member.year > 0 ? member.year : "-",
      member.lastSession != "" ? formatDate(member.lastSession) : "-",
      member.membershipType === "admin" ? "advisor" : member.membershipType)
  )

  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof Data>('membershipType');
  const [selected, setSelected] = useState<string>("");
  const [page, setPage] = useState(0);
  const [dense, setDense] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortedData, setSortedData] = useState<Data[]>(rows);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const [loading, setloading] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMembershipTypes, setSelectedMembershipTypes] = useState<string[]>(['advisor', 'student']);
  const membershipTypes = [ 'advisor', 'student'];

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const handleMembershipTypeChange = (type: string) => {
    const value = type;
    console.log(value);
    setSelectedMembershipTypes((prev) =>
      prev.includes(value) ? prev.filter((type) => type !== value) : [...prev, value]
    );
  };

  // Handle search query change
  const handleSearchChange = (e: any) => {
    setSearchQuery(e.target.value);
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleClick = (event: React.MouseEvent<unknown>, id: string) => {
    const isSelected = selected === id;
    let newSelected: string = "";

    if (!isSelected) {
      newSelected = id;
    } else if (isSelected) {
      newSelected = "";
    }
    setSelected(newSelected);
    if(props.memberSelected){
    if(newSelected !== ""){
      props.memberSelected(props.members.filter(x => x.email === id && x.id !== null)[0]||null);
    }
    else{
      props.memberSelected(null);
    }
  }
     
  };

  const deleteMember = (id: string) => {
    if (props.deleteMembers) {
      props.deleteMembers(props.members.filter(x => x.email === id));
      setloading(true);
    }
  }

  const remindMember = (id: string) => {
    if (props.remindMembers) {
      props.remindMembers(props.members.filter(x => x.email === id));
    }
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - sortedData.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      [...sortedData]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, sortedData],
  );

  useEffect(() => {
    if (props.setoverlayComp) {
      props.setoverlayComp(loading);
    }
  }, [loading]);

  useEffect(() => {
    let nrows = props.members.map(member =>
      createData(member.email,
        member.name,
        member.id ? "active" : "invited",
        member.topPath != "" ? member.topPath : "-",
        member.school != "" ? member.school : "-",
        member.year > 0 ? member.year : "-",
        member.lastSession != "" ? formatDate(member.lastSession) : "-",
        member.membershipType === "admin" ? "advisor" : member.membershipType)
    )
    setSortedData(nrows);
    setloading(false);
  }, [props.members]);

  useEffect(() => {
    if (searchQuery !== "") {
      const filteredData = sortedData.filter(item =>
        item.school.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.user.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.topPath.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.year.toString().includes(searchQuery.toLowerCase())
      );
      setSortedData(filteredData);
    }
    
    else {
      let filteredRows = rows.filter((row) =>
        selectedMembershipTypes.length === 0 || selectedMembershipTypes.includes(row.membershipType)
      );
      setSortedData(filteredRows);
    }
  }, [searchQuery]);

  useEffect(() => {
    let filteredRows = rows.filter((row) =>
      selectedMembershipTypes.length === 0 || selectedMembershipTypes.includes(row.membershipType)
    );
    setSortedData(filteredRows);
  }, [selectedMembershipTypes]);

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <MembersTableToolbar
          heading={props.isInviteTable ? "Members to Invite" : "All Members"}
          searchBar={
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              slotProps={{
                input: {
                  endAdornment: (
                    <IconButton type="button" aria-label="search" size="small">
                      <SearchIcon />
                    </IconButton>
                  ),
                  sx: { pr: 0.5 },
                },
              }}
              sx={{ display: { md: 'inline-block' }, mr: 1 }}
              value={searchQuery}
              onChange={handleSearchChange}
            />
          }
          filterBar={
            <>
              <IconButton
                aria-label="filter list"
                aria-controls="filter-menu"
                aria-haspopup="true"
                onClick={handleFilterClick}
              >
                <FilterListIcon />
              </IconButton>
              <Menu
                id="filter-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleFilterClose}
              >
                {membershipTypes.map((type) => (
                  <div className="flex px-2 items-center justify-center" onClick={() => handleMembershipTypeChange(type)} key={type}>
                    <Checkbox
                      checked={selectedMembershipTypes.includes(type)}
                      value={type}
                    />
                    <ListItemText primary={type} />
                  </div>
                ))}
              </Menu>
            </>
          }
        />
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={dense ? 'small' : 'medium'}
          >
            <MembersTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={sortedData.length}
              isInviteTable={props.isInviteTable || false}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const isItemSelected = selected === row.id;
                return (
                  <StyledTableRow
                    onMouseEnter={() => setHoveredRow(row.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                    onClick={(event) => handleClick(event, row.id)}
                    role="checkbox"
                    tabIndex={-1}
                    key={row.id}
                    title={row.id}
                    selected={isItemSelected}
                    sx={{ cursor: 'pointer' }}
                  >
                    <StyledTableCell align="left">{row.user}
                      {row.memberStatus === "invited" &&!props.isInviteTable && (
                        <>
                          <IconButton size='small' title='delete unused invite' onClick={() => deleteMember(row.id)}>
                            <Cancel sx={{ fontSize: '20px', color:'red' }} />
                          </IconButton>
                          <IconButton size='small' title='send reminder email' onClick={() => remindMember(row.id)}>
                            <Notifications sx={{ fontSize: '20px', color:'gold' }} />
                          </IconButton>
                        </>
                      )}
                    </StyledTableCell>
                    {props.isInviteTable && <StyledTableCell align="left">{row.id}</StyledTableCell>}
                    {!props.isInviteTable && <StyledTableCell align="left">{row.memberStatus}</StyledTableCell>}
                    <StyledTableCell align="left">{row.membershipType}</StyledTableCell>
                    {!props.isInviteTable && <StyledTableCell align="left">{row.topPath}</StyledTableCell>}
                    <StyledTableCell align="left">{row.school}</StyledTableCell>
                    <StyledTableCell align="left">{row.year}</StyledTableCell>
                    {!props.isInviteTable && <StyledTableCell align="left">{row.lastSession}</StyledTableCell>}
                  </StyledTableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: (dense ? 33 : 53) * emptyRows,
                  }}
                >
                  <StyledTableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={sortedData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
}